import React, { Fragment, useEffect, useMemo } from "react";
import "./App.css";
import {
    ScreenActions,
    useTypedSelector,
    RootDispatch,
    AuthActions,
    store,
    ConfigurationActions,
} from "./redux";
import { ScreenLoader } from "./components/ScreenRenderer";
import { Grid, Container, Button, Box, Link as MuiLink, styled } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { Route, Link, useNavigate, Routes } from "react-router-dom";
import { Overview } from "./screens/Overview";
import CssBaseline from "@material-ui/core/CssBaseline";
import Syna from "./assets/images/syna_logo.svg";
import SynaFooterLogo from "./assets/images/syna-footer-logo.svg";
import SuewagFooterLogo from "./assets/images/suewag-footer-logo.svg";
import BackIcon from "./assets/images/back_icon.svg";
import { InitializerIndicator } from "./components/InitializerIndicator";
import { InterceptableComponent } from "./components/core/InterceptableRoute";
import { ChangeNames, ChangeSet, OperationState } from "./clients/FirebaseClient/types";
import { Colors } from "./styles";
import config from "./config";

const App: React.FC = () => {
    const year = useMemo(() => new Date().getFullYear(), []);
    const dispatch: RootDispatch = useDispatch();
    const navigate = useNavigate();
    const menuVisible = useTypedSelector((state) => state.screen.menuVisible);
    const uid = useTypedSelector((state) => state.auth.uid);
    const waitingForAuthEvent = useTypedSelector((state) => state.auth.waitingForAuthEvent);
    const stateParam = new URL(window.location.href).searchParams.get("state");
    const SuewagLogo = styled('img')(() => ({
        width: 120,
        height: 40,
        marginTop: 16,
        marginRight: 28,
        float: "right",
    }));
    SuewagLogo.defaultProps = {
        src: SuewagFooterLogo,
        alt: "Süwag-Logo",
    };

    useEffect(() => {
        const id = setTimeout(() => {
            if (store.getState().auth.waitingForAuthEvent) {
                dispatch(AuthActions.authTimedOut());
            }
        }, 5000);
        return () => clearTimeout(id);
    }, [dispatch]);

    // check for initial state in url
    useEffect(() => {
        if (!uid || !stateParam) return;

        (async () => {
            try {
                dispatch(ConfigurationActions.setCreating(true));
                console.log("found data in url");
                const state: Required<ChangeSet<ChangeNames.BUILDING_ADDRESS>> = JSON.parse(
                    decodeURI(stateParam),
                );
                const operationResult = await dispatch(
                    ConfigurationActions.initializeOperation(state),
                );
                if (operationResult.status === OperationState.SUCCEEDED) {
                    navigate(`/configuration/${operationResult.result}`, { replace: true });
                    return;
                }
            } finally {
                dispatch(ConfigurationActions.setCreating(false));
            }
        })();
    }, [uid, stateParam, dispatch, navigate]);

    return (
        <Fragment>
            <CssBaseline />
            {["Review", "Staging"].includes(config.environment) && (
                <Banner>
                    Sie befinden sich auf einer{" "}
                    {config.environment === "Staging" ? "Testumgebung" : "Entwicklungsumgebung"}!
                    Anträge, die Sie hier erstellen, werden nicht bearbeitet. Unsere offizielle
                    Webseite finden Sie unter{" "}
                    <MuiLink href={`https://meine.syna.de${process.env.PUBLIC_URL}`}>
                        <Box component="span" color="#f59b00">
                            meine.syna.de
                        </Box>
                    </MuiLink>
                </Banner>
            )}
            <div className="App">
                <Container className="AppBody">
                    <InitializerIndicator>
                        <Container
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                                padding: 0,
                                flexBasis: "auto",
                                minHeight: "calc(100vh - 168px)",
                            }}
                        >
                            <Grid item>
                                <Box sx={{ display: { xs: "block", md: "none" } }}>
                                    <header className="App-header App-header-mobile">
                                        {!menuVisible ? (
                                            <Button
                                                startIcon={<img alt="" src={BackIcon} />}
                                                onClick={() =>
                                                    dispatch(ScreenActions.setMenuVisible(true))
                                                }
                                            >
                                                Menü
                                            </Button>
                                        ) : (
                                            <div />
                                        )}
                                        <Link to="/">
                                            <img
                                                alt=""
                                                src={Syna}
                                                style={{ width: 112, height: 38 }}
                                            />
                                        </Link>
                                        <div style={{ flexBasis: "100%", height: 0 }}></div>
                                        <div style={{ width: 1 }} />
                                    </header>
                                </Box>
                                <Box sx={{ display: { xs: "none", md: "block" } }}>
                                    <header className="App-header">
                                        <div style={{ flex: 1 / 3 }}></div>
                                        <div
                                            style={{
                                                flex: 1 / 3,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Link to="/">
                                                <img
                                                    alt=""
                                                    src={Syna}
                                                    style={{
                                                        width: 166,
                                                        height: 60,
                                                    }}
                                                />
                                            </Link>
                                        </div>
                                        <div
                                            style={{
                                                flex: 1 / 3,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-end",
                                            }}
                                        >
                                        </div>
                                    </header>
                                </Box>
                            </Grid>
                            {!waitingForAuthEvent && (
                                <Routes>
                                    <Route
                                        index
                                        element={
                                            <InterceptableComponent
                                                shouldIntercept={(iProps) =>
                                                    (
                                                        !iProps.uid
                                                    )
                                                }
                                                interceptor={async (iProps) => {
                                                    if (!iProps.uid) {
                                                        console.log(
                                                            "unauthenticated, redirecting to login",
                                                        );
                                                        dispatch(AuthActions.signInWithMSP());
                                                        return;
                                                    }
                                                }}
                                            >
                                                <Overview />
                                            </InterceptableComponent>
                                        }
                                    />
                                    <Route
                                        path="configuration/:confId"
                                        element={
                                            <InterceptableComponent
                                                shouldIntercept={(iProps) =>
                                                    !iProps.uid ||
                                                    !iProps.params?.["confId"] ||
                                                    iProps.configurationId !==
                                                    iProps.params["confId"] ||
                                                    !iProps.configurations[iProps.params["confId"]]
                                                }
                                                interceptor={async (iProps) => {
                                                    if (!iProps.uid) {
                                                        console.log(
                                                            "not logged in, redirectig to login",
                                                        );
                                                        dispatch(AuthActions.signInWithMSP());
                                                        return;
                                                    }
                                                    if (
                                                        !iProps.params?.["confId"] ||
                                                        !iProps.configurations[
                                                            iProps.params["confId"]
                                                            ]
                                                    ) {
                                                        console.log(
                                                            "no confId in URL or the provided one is not found, back to start",
                                                            iProps.params?.["confId"],
                                                        );
                                                        iProps.navigate("/");
                                                    }
                                                    if (
                                                        iProps.params?.["confId"] !==
                                                        iProps.configurationId
                                                    ) {
                                                        console.log(
                                                            "confId in URL isn't selected yet, selecting",
                                                        );
                                                        dispatch(
                                                            ConfigurationActions.load(
                                                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                                                iProps.params!["confId"],
                                                            ),
                                                        );
                                                        return;
                                                    }
                                                }}
                                            >
                                                <ScreenLoader />
                                            </InterceptableComponent>
                                        }
                                    />
                                    <Route
                                        path="*"
                                        element={
                                            <InterceptableComponent
                                                shouldIntercept={() => true}
                                                interceptor={async (iProps) => {
                                                    iProps.navigate("/");
                                                }}
                                            >
                                                <></>
                                            </InterceptableComponent>
                                        }
                                    />
                                </Routes>
                            )}
                        </Container>
                        <Grid item>
                            <footer>
                                <div className="footerContainer">
                                    <div className="footerTop">
                                        <img
                                            alt="Syna-Logo"
                                            src={SynaFooterLogo}
                                            style={{
                                                marginTop: 24,
                                                marginLeft: 28,
                                            }}
                                        />
                                        <SuewagLogo />
                                    </div>
                                    <div className="footerBottom">
                                        <div style={{ marginLeft: 28 }}>
                                            <a
                                                className="footerLinks"
                                                href="https://www.syna.de/corp/impressum"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Impressum
                                            </a>
                                        </div>
                                        <div style={{ marginLeft: 24 }}>
                                            <a
                                                className="footerLinks"
                                                href="https://www.syna.de/corp/disclaimer"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Disclaimer
                                            </a>
                                        </div>
                                        <div style={{ marginLeft: 24 }}>
                                            <a
                                                className="footerLinks"
                                                href="https://www.syna.de/corp/datenschutz"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Datenschutz
                                            </a>
                                        </div>
                                        {}
                                        <div className="footerLinks" style={{ marginLeft: "auto" }}>
                                            <div style={{ marginRight: 28 }}>© Syna {year}</div>
                                        </div>
                                    </div>
                                </div>
                            </footer>
                        </Grid>
                    </InitializerIndicator>
                </Container>
            </div>
        </Fragment>
    );
};

const Banner = (props: { children: React.ReactNode }) => {
    return (
        <Box
            py="14px"
            px="16px"
            bgcolor="#b92c0e"
            boxSizing="content-box"
            fontFamily="innogyWEB"
            fontSize="20px"
            fontWeight="300"
            color={Colors.white}
            lineHeight="normal"
            letterSpacing="0"
            textAlign="center"
        >
            {props.children}
        </Box>
    );
};

export default App;
